import type { Order, OwnerOrder, Role } from "../shared/order-domain";

export type VisibleOrder = Order | OwnerOrder;
export interface Session { role: Role; label: string }
export interface UploadedFile { key: string; name: string }

const ERROR_MESSAGES: Record<string, string> = {
  invalid_code: "访问代号不正确", unauthorized: "登录已过期，请重新登录", forbidden: "当前身份无权执行这个操作",
  server_auth_not_configured: "服务端尚未配置访问代号", order_not_found: "订单不存在，请刷新列表",
  order_already_shipped: "订单已发货，不能再修改打印文件或地址", order_already_paid: "订单已结清，不能再修改或重复确认",
  shipment_required: "请先填写快递单号和报价", file_not_uploaded: "文件尚未上传成功，请重新添加文件",
  file_in_use: "文件已被订单使用", invalid_file_type: "请上传支持的打印文件", invalid_file_size: "文件不能为空，且不能超过 100 MB",
  internal_error: "保存失败，请稍后重试", invalid_item: "文件条目不属于当前订单",
  receipt_changed: "收款记录已变更，请刷新订单后重试",
  address_changed: "收货地址已变更，请刷新后重新复制",
  invalid_color: "请选择黑色哑光PETG或白色PETG；已有文件可保留原材料",
  quote_changed: "报价已变更，请关闭窗口并刷新订单后重新核对金额",
};

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) { super(message); }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, { ...options, credentials: "same-origin" });
  } catch {
    throw new Error("无法连接服务，请检查网络后重试");
  }
  const data = await response.json() as { error?: string; issues?: Array<{ message: string }> };
  if (!response.ok) {
    if (response.status === 401 && path !== "/auth/login") window.dispatchEvent(new Event("sessionexpired"));
    throw new ApiError(response.status, ERROR_MESSAGES[data.error ?? ""] ?? data.issues?.[0]?.message ?? "操作失败，请重试");
  }
  return data as T;
}

export function write<T>(path: string, method: string, body: unknown): Promise<T> {
  return request<T>(path, { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
}

export async function uploadFile(file: File): Promise<UploadedFile> {
  const body = new FormData();
  body.append("file", file);
  return request<UploadedFile>("/files", { method: "POST", body });
}

export function discardFile(key: string): Promise<{ ok: boolean }> {
  return request(`/files/${encodeURIComponent(key)}`, { method: "DELETE" });
}
